import { UntypedFormBuilder, UntypedFormGroup, Validators } from "@angular/forms";
import { BaseFormBuilder } from "src/app/core/formBuilders/BaseFormBuilder";
import { CheckCostEstimationResult } from "./CheckCostEstimationResult";
import { CostEstimationPart } from "./CostEstimationPart";
import { CostEstimationStatus } from "./CostEstimationStatus";
import { CostEstimationType } from "./CostEstimationType";
import { Constants } from "src/app/shared/model/Constants";

export interface CostEstimation {
    id: number,
    claimIdentifier: string;
    costEstimationTypeId: number;
    titleDescription?: string;
    costEstimationStatusId: number;
    pdrLabour: number;
    carBodyLabour: number;
    additionalCosts: number;
    paintLabour: number;
    paintMaterials: number;
    originalParts: number;
    alternativeParts: number;
    normalies: number;
    discountForParts:number;
    discountAmount: number;
    finalNetAmount: number;
    deductibleAmount: number;
    grosAmount: number;
    sentBy?: string;
    sentDate?: string;
    sentAmount?: number;
    acceptedBy?: string;
    acceptedDate?: string;
    pdrLabourAccepted?: number;
    carBodyLabourAccepted?: number;
    additionalCostsAccepted?: number;
    paintLabourAccepted?: number;
    paintMaterialsAccepted?: number;
    originalPartsAccepted?: number;
    alternativePartsAccepted?: number;
    normaliesAccepted?: number;
    discountForPartsAccepted?:number;
    discountAmountAccepted?: number;
    finalNetAmountAccepted?: number;
    grosAmountAccepted?: number;
    deductibleAmountAccepted?: number;

    pdrLabourForInvoice?: number;
    carBodyLabourForInvoice?: number;
    additionalCostsForInvoice?: number;
    paintLabourForInvoice?: number;
    paintMaterialsForInvoice?: number;
    originalPartsForInvoice?: number;
    alternativePartsForInvoice?: number;
    normaliesForInvoice?: number;
    discountAmountForInvoice?: number;
    discountForPartsForInvoice?:number;
    netAmountForInvoice?: number;
    vatAmountForInvoice?: number;
    finalNetAmountForInvoice?: number;
    grosAmountForInvoice?: number;
    deductibleAmountForInvoice?: number;

    isInvoicePartialNeeded?: boolean;
    pdrLabourForInvoicePartial?: number;
    carBodyLabourForInvoicePartial?: number;
    additionalCostsForInvoicePartial?: number;
    paintLabourForInvoicePartial?: number;
    paintMaterialsForInvoicePartial?: number;
    originalPartsForInvoicePartial?: number;
    alternativePartsForInvoicePartial?: number;
    normaliesForInvoicePartial?: number;
    discountAmountForInvoicePartial?: number;
    discountForPartsForInvoicePartial?:number;
    netAmountForInvoicePartial?: number;
    vatAmountForInvoicePartial?: number;
    finalNetAmountForInvoicePartial?: number;
    grosAmountForInvoicePartial?: number;
    deductibleAmountForInvoicePartial?: number;

    acceptedAmount?: number;
    vatAmountAccepted?: number;
    rejectedBy?: string;
    rejectedDate?: string;
    netAmount: number;
    vatAmount: number;
    createdBy?: string;
    timestamp?: string;
    remarks?: string;
    isPartiallyAccepted?: boolean;
    partiallyAcceptedReason?: string;
    audanetTaskId?: string;
    audanetCalculationId?: string;
    datCaseId?: number;
    passToPartnerState?: boolean;
    applyDiscountOutOfCostEstimation:boolean;
    forInvoiceConfirmation? : boolean;
		forInvoiceConfirmedBy? : string;
		forInvoiceConfirmationDate?:string;
    costEstimationParts?: CostEstimationPart[];

    carBrand? : string;
    carModel? : string;
    carPlateNumber? : string;
    claimNumber? : string;

    pdrLabourForInvoiceTotal?: number;
    carBodyLabourForInvoiceTotal?: number;
    additionalCostsForInvoiceTotal?: number;
    paintLabourForInvoiceTotal?: number;
    paintMaterialsForInvoiceTotal?: number;
    originalPartsForInvoiceTotal?: number;
    alternativePartsForInvoiceTotal?: number;
    normaliesForInvoiceTotal?: number;
    discountAmountForInvoiceTotal?: number;
    discountForPartsForInvoiceTotal?:number;
    netAmountForInvoiceTotal?: number;
    vatAmountForInvoiceTotal?: number;
    finalNetAmountForInvoiceTotal?: number;
    grosAmountForInvoiceTotal?: number;
    deductibleAmountForInvoiceTotal?: number;
    inspectionCardTotalAmount?: number;
}

export class CostEstimationFormBuilder extends BaseFormBuilder {
 
    static getForm(fb: UntypedFormBuilder, costEstimation: CostEstimation) {

      return fb.group(
        {
          id: [costEstimation.id],
          audanetTaskId: [costEstimation.audanetTaskId],
          audanetCalculationId: [costEstimation.audanetCalculationId],
          datCaseId: [costEstimation.datCaseId],
          passToPartnerState: [costEstimation.passToPartnerState],
          claimIdentifier: [costEstimation.claimIdentifier],
          costEstimationTypeId: [{value:costEstimation.costEstimationTypeId,disabled:true} ,Validators.min(1)],
          titleDescription: [costEstimation.titleDescription],
          costEstimationStatusId: [{value:costEstimation.costEstimationStatusId,disabled:true},Validators.min(1)],
          pdrLabour: [costEstimation.pdrLabour,[Validators.required,Validators.min(0)]],
          carBodyLabour: [costEstimation.carBodyLabour,[Validators.required,Validators.min(0)]],
          additionalCosts: [costEstimation.additionalCosts,[Validators.required,Validators.min(0)]],
          paintLabour: [costEstimation.paintLabour,[Validators.required,Validators.min(0)]],
          paintMaterials: [costEstimation.paintMaterials,[Validators.required,Validators.min(0)]],
          originalParts: [costEstimation.originalParts,[Validators.required,Validators.min(0)]],
          alternativeParts: [costEstimation.alternativeParts,[Validators.required,Validators.min(0)]],
          normalies: [costEstimation.normalies,[Validators.required,Validators.min(0)]],
          discountForParts: [costEstimation.discountForParts,[Validators.required,Validators.min(0)]],
          discountAmount: [costEstimation.discountAmount,[Validators.required,Validators.min(0)]],
          netAmount: [costEstimation.netAmount,[Validators.required,Validators.min(0)]],
          vatAmount: [costEstimation.vatAmount,[Validators.min(0)]],
          finalNetAmount: [{value: costEstimation.finalNetAmount,disabled:true},[Validators.required,Validators.min(0)]],
          deductibleAmount: [costEstimation.deductibleAmount,[Validators.required,Validators.min(0)]],
          grosAmount: [{value: costEstimation.grosAmount,disabled:true},[Validators.required,Validators.min(0)]],

          pdrLabourAccepted: [costEstimation.pdrLabourAccepted,[Validators.min(0)]],
          carBodyLabourAccepted: [costEstimation.carBodyLabourAccepted,[Validators.min(0)]],
          additionalCostsAccepted: [costEstimation.additionalCostsAccepted,[Validators.min(0)]],
          paintLabourAccepted: [costEstimation.paintLabourAccepted,[Validators.min(0)]],
          paintMaterialsAccepted: [costEstimation.paintMaterialsAccepted,[Validators.min(0)]],
          originalPartsAccepted: [costEstimation.originalPartsAccepted,[Validators.min(0)]],
          alternativePartsAccepted: [costEstimation.alternativePartsAccepted,[Validators.min(0)]],
          normaliesAccepted: [costEstimation.normaliesAccepted,[Validators.min(0)]],
          discountForPartsAccepted: [costEstimation.discountForPartsAccepted,[Validators.min(0)]],
          acceptedAmount: [costEstimation.acceptedAmount,[Validators.min(0)]],
          vatAmountAccepted: [costEstimation.vatAmountAccepted,[Validators.min(0)]],
          discountAmountAccepted: [costEstimation.discountAmountAccepted,[Validators.min(0)]],
          finalNetAmountAccepted: [{value:costEstimation.finalNetAmountAccepted,disabled:true},[Validators.min(0)]],
          deductibleAmountAccepted: [ costEstimation.deductibleAmountAccepted,[Validators.min(0)]],
          grosAmountAccepted: [{value: costEstimation.grosAmountAccepted,disabled:true},[Validators.required,Validators.min(0)]],

          pdrLabourForInvoice: [costEstimation.pdrLabourForInvoice,[Validators.min(0)]],
          carBodyLabourForInvoice: [costEstimation.carBodyLabourForInvoice,[Validators.min(0)]],
          additionalCostsForInvoice: [costEstimation.additionalCostsForInvoice,[Validators.min(0)]],
          paintLabourForInvoice: [costEstimation.paintLabourForInvoice,[Validators.min(0)]],
          paintMaterialsForInvoice: [costEstimation.paintMaterialsForInvoice,[Validators.min(0)]],
          originalPartsForInvoice: [costEstimation.originalPartsForInvoice,[Validators.min(0)]],
          alternativePartsForInvoice: [costEstimation.alternativePartsForInvoice,[Validators.min(0)]],
          normaliesForInvoice: [costEstimation.normaliesForInvoice,[Validators.min(0)]],
          netAmountForInvoice: [costEstimation.netAmountForInvoice,[Validators.min(0)]],
          vatAmountForInvoice: [costEstimation.vatAmountForInvoice,[Validators.min(0)]],
          discountAmountForInvoice: [costEstimation.discountAmountForInvoice,[Validators.min(0)]],
          discountForPartsForInvoice: [costEstimation.discountForPartsForInvoice,[Validators.min(0)]],
          finalNetAmountForInvoice: [costEstimation.finalNetAmountForInvoice,[Validators.min(0)]],
          grosAmountForInvoice : [costEstimation.grosAmountForInvoice,[Validators.min(0)]],
          deductibleAmountForInvoice : [costEstimation.deductibleAmountForInvoice,[Validators.min(0)]],

          isInvoicePartialNeeded: [costEstimation.isInvoicePartialNeeded],
          pdrLabourForInvoicePartial: [costEstimation.pdrLabourForInvoicePartial,[Validators.min(0)]],
          carBodyLabourForInvoicePartial: [costEstimation.carBodyLabourForInvoicePartial,[Validators.min(0)]],
          additionalCostsForInvoicePartial: [costEstimation.additionalCostsForInvoicePartial,[Validators.min(0)]],
          paintLabourForInvoicePartial: [costEstimation.paintLabourForInvoicePartial,[Validators.min(0)]],
          paintMaterialsForInvoicePartial: [costEstimation.paintMaterialsForInvoicePartial,[Validators.min(0)]],
          originalPartsForInvoicePartial: [costEstimation.originalPartsForInvoicePartial,[Validators.min(0)]],
          alternativePartsForInvoicePartial: [costEstimation.alternativePartsForInvoicePartial,[Validators.min(0)]],
          normaliesForInvoicePartial: [costEstimation.normaliesForInvoicePartial,[Validators.min(0)]],
          netAmountForInvoicePartial: [costEstimation.netAmountForInvoicePartial,[Validators.min(0)]],
          vatAmountForInvoicePartial: [costEstimation.vatAmountForInvoicePartial,[Validators.min(0)]],
          discountAmountForInvoicePartial: [costEstimation.discountAmountForInvoicePartial,[Validators.min(0)]],
          discountForPartsForInvoicePartial: [costEstimation.discountForPartsForInvoicePartial,[Validators.min(0)]],
          finalNetAmountForInvoicePartial: [costEstimation.finalNetAmountForInvoicePartial,[Validators.min(0)]],
          grosAmountForInvoicePartial : [costEstimation.grosAmountForInvoicePartial,[Validators.min(0)]],
          deductibleAmountForInvoicePartial : [costEstimation.deductibleAmountForInvoicePartial,[Validators.min(0)]],

          remarks: [costEstimation.remarks],
          isPartiallyAccepted: [costEstimation.isPartiallyAccepted],
          partiallyAcceptedReason: [costEstimation.partiallyAcceptedReason],
          applyDiscountOutOfCostEstimation: [costEstimation.applyDiscountOutOfCostEstimation],
          forInvoiceConfirmation : [costEstimation.forInvoiceConfirmation],
          forInvoiceConfirmedBy : [costEstimation.forInvoiceConfirmedBy],
          forInvoiceConfirmationDate : [costEstimation.forInvoiceConfirmationDate],
          
          filesToAttach: [[]],
          costEstimationParts: [costEstimation.costEstimationParts]
        });
    }

    static CopyValuesToAccepted(form: UntypedFormGroup) {
        form.controls['pdrLabourAccepted'].setValue( form.controls['pdrLabour'].value );
        form.controls['carBodyLabourAccepted'].setValue( form.controls['carBodyLabour'].value );
        form.controls['additionalCostsAccepted'].setValue( form.controls['additionalCosts'].value );
        form.controls['paintLabourAccepted'].setValue( form.controls['paintLabour'].value );
        form.controls['paintMaterialsAccepted'].setValue( form.controls['paintMaterials'].value );
        form.controls['originalPartsAccepted'].setValue( form.controls['originalParts'].value );
        form.controls['alternativePartsAccepted'].setValue( form.controls['alternativeParts'].value );
        form.controls['normaliesAccepted'].setValue( form.controls['normalies'].value );
        form.controls['discountForPartsAccepted'].setValue( form.controls['discountForParts'].value );
        form.controls['discountAmountAccepted'].setValue( form.controls['discountAmount'].value );
        form.controls['acceptedAmount'].setValue( form.controls['netAmount'].value );
        form.controls['deductibleAmountAccepted'].setValue( form.controls['deductibleAmount'].value );
        form.controls['finalNetAmountAccepted'].setValue( form.controls['finalNetAmount'].value );
        form.controls['vatAmountAccepted'].setValue( form.controls['vatAmount'].value );
        form.controls['grosAmountAccepted'].setValue( form.controls['grosAmount'].value );

      }

      static CopyValuesToForInvoice(form: UntypedFormGroup) {
        form.controls['pdrLabourForInvoice'].setValue( form.controls['pdrLabourAccepted'].value );
        form.controls['carBodyLabourForInvoice'].setValue( form.controls['carBodyLabourAccepted'].value );
        form.controls['additionalCostsForInvoice'].setValue( form.controls['additionalCostsAccepted'].value );
        form.controls['paintLabourForInvoice'].setValue( form.controls['paintLabourAccepted'].value );
        form.controls['paintMaterialsForInvoice'].setValue( form.controls['paintMaterialsAccepted'].value );
        form.controls['originalPartsForInvoice'].setValue( form.controls['originalPartsAccepted'].value );
        form.controls['alternativePartsForInvoice'].setValue( form.controls['alternativePartsAccepted'].value );
        form.controls['normaliesForInvoice'].setValue( form.controls['normaliesAccepted'].value );
        form.controls['discountForPartsForInvoice'].setValue( form.controls['discountForPartsAccepted'].value );
        form.controls['discountAmountForInvoice'].setValue( form.controls['discountAmountAccepted'].value );
        form.controls['netAmountForInvoice'].setValue( form.controls['acceptedAmount'].value );
        form.controls['deductibleAmountForInvoice'].setValue( form.controls['deductibleAmountAccepted'].value );
        form.controls['finalNetAmountForInvoice'].setValue( form.controls['finalNetAmountAccepted'].value );
        form.controls['vatAmountForInvoice'].setValue( form.controls['vatAmountAccepted'].value );
        form.controls['grosAmountForInvoice'].setValue( form.controls['grosAmountAccepted'].value );

      }

      static CopyValuesToForInvoicePartial(form: UntypedFormGroup) {
        form.controls['pdrLabourForInvoicePartial'].setValue( form.controls['pdrLabourAccepted'].value );
        form.controls['carBodyLabourForInvoicePartial'].setValue( form.controls['carBodyLabourAccepted'].value );
        form.controls['additionalCostsForInvoicePartial'].setValue( form.controls['additionalCostsAccepted'].value );
        form.controls['paintLabourForInvoicePartial'].setValue( form.controls['paintLabourAccepted'].value );
        form.controls['paintMaterialsForInvoicePartial'].setValue( form.controls['paintMaterialsAccepted'].value );
        form.controls['originalPartsForInvoicePartial'].setValue( form.controls['originalPartsAccepted'].value );
        form.controls['alternativePartsForInvoicePartial'].setValue( form.controls['alternativePartsAccepted'].value );
        form.controls['normaliesForInvoicePartial'].setValue( form.controls['normaliesAccepted'].value );
        form.controls['discountForPartsForInvoicePartial'].setValue( form.controls['discountForPartsAccepted'].value );
        form.controls['discountAmountForInvoicePartial'].setValue( form.controls['discountAmountAccepted'].value );
        form.controls['netAmountForInvoicePartial'].setValue( form.controls['acceptedAmount'].value );
        form.controls['finalNetAmountForInvoicePartial'].setValue( form.controls['finalNetAmountAccepted'].value );
        form.controls['deductibleAmountForInvoicePartial'].setValue( form.controls['deductibleAmountAccepted'].value );
        form.controls['vatAmountForInvoicePartial'].setValue( form.controls['vatAmountAccepted'].value );
        form.controls['grosAmountForInvoicePartial'].setValue( form.controls['grosAmountAccepted'].value );

      }

      static CleanValuesToForInvoicePartial(form: UntypedFormGroup) {
        form.controls['pdrLabourForInvoicePartial'].setValue( 0 );
        form.controls['carBodyLabourForInvoicePartial'].setValue( 0 );
        form.controls['additionalCostsForInvoicePartial'].setValue( 0 );
        form.controls['paintLabourForInvoicePartial'].setValue( 0 );
        form.controls['paintMaterialsForInvoicePartial'].setValue( 0 );
        form.controls['originalPartsForInvoicePartial'].setValue( 0 );
        form.controls['alternativePartsForInvoicePartial'].setValue( 0 );
        form.controls['normaliesForInvoicePartial'].setValue( 0 );
        form.controls['discountForPartsForInvoicePartial'].setValue( 0 );
        form.controls['discountAmountForInvoicePartial'].setValue( 0 );
        form.controls['netAmountForInvoicePartial'].setValue( 0 );
        form.controls['finalNetAmountForInvoicePartial'].setValue( 0 );
        form.controls['deductibleAmountForInvoicePartial'].setValue( 0 );
        form.controls['vatAmountForInvoicePartial'].setValue( 0 );
        form.controls['grosAmountForInvoicePartial'].setValue( 0 );

      }

      static CalculateForInvoiceValues(form: UntypedFormGroup) {
        form.controls['pdrLabourForInvoice'].setValue( form.controls['pdrLabourAccepted'].value - form.controls['pdrLabourForInvoicePartial'].value );
        form.controls['carBodyLabourForInvoice'].setValue( form.controls['carBodyLabourAccepted'].value - form.controls['carBodyLabourForInvoicePartial'].value);
        form.controls['additionalCostsForInvoice'].setValue( form.controls['additionalCostsAccepted'].value - form.controls['additionalCostsForInvoicePartial'].value);
        form.controls['paintLabourForInvoice'].setValue( form.controls['paintLabourAccepted'].value - form.controls['paintLabourForInvoicePartial'].value);
        form.controls['paintMaterialsForInvoice'].setValue( form.controls['paintMaterialsAccepted'].value - form.controls['paintMaterialsForInvoicePartial'].value);
        form.controls['originalPartsForInvoice'].setValue( form.controls['originalPartsAccepted'].value - form.controls['originalPartsForInvoicePartial'].value);
        form.controls['alternativePartsForInvoice'].setValue( form.controls['alternativePartsAccepted'].value - form.controls['alternativePartsForInvoicePartial'].value);
        form.controls['normaliesForInvoice'].setValue( form.controls['normaliesAccepted'].value - form.controls['normaliesForInvoicePartial'].value);
        form.controls['discountForPartsForInvoice'].setValue( form.controls['discountForPartsAccepted'].value - form.controls['discountForPartsForInvoicePartial'].value);
        form.controls['discountAmountForInvoice'].setValue( form.controls['discountAmountAccepted'].value - form.controls['discountAmountForInvoicePartial'].value);
        form.controls['netAmountForInvoice'].setValue( form.controls['acceptedAmount'].value - form.controls['netAmountForInvoicePartial'].value);
        form.controls['deductibleAmountForInvoice'].setValue( form.controls['deductibleAmountAccepted'].value - form.controls['deductibleAmountForInvoicePartial'].value);
        form.controls['finalNetAmountForInvoice'].setValue( form.controls['finalNetAmountAccepted'].value - form.controls['finalNetAmountForInvoicePartial'].value);
        form.controls['vatAmountForInvoice'].setValue( form.controls['vatAmountAccepted'].value - form.controls['vatAmountForInvoicePartial'].value);
        form.controls['grosAmountForInvoice'].setValue( form.controls['grosAmountAccepted'].value - form.controls['grosAmountForInvoicePartial'].value);

      }

      static CheckNetSumValue(form: UntypedFormGroup) : CheckCostEstimationResult {

        let net = form.controls['netAmount'].value;

        let sum = 0;
        sum += form.controls['pdrLabour'].value;
        sum += form.controls['carBodyLabour'].value;
        sum += form.controls['additionalCosts'].value;
        sum += form.controls['paintLabour'].value;
        sum += form.controls['paintMaterials'].value;
        sum += form.controls['originalParts'].value;
        sum += form.controls['alternativeParts'].value;
        sum += form.controls['normalies'].value;
        sum -= form.controls['discountForParts'].value;
        

        const discountOutOfEstimation = form.controls['applyDiscountOutOfCostEstimation'].value;
        if(!discountOutOfEstimation){
          sum -= form.controls['discountAmount'].value;
        }
 
        sum = Math.round(sum * 100) / 100;

        if(!net){
          net = 0;
        }

        const deductibles = form.controls['deductibleAmount'].value;
        const vat = ((net - deductibles) * Constants.DefaultVatRate *100) / 100;

        form.controls['finalNetAmount'].setValue(net - deductibles);
        form.controls['vatAmount'].setValue(vat);
        form.controls['grosAmount'].setValue(net - deductibles + vat);


        return {
          mismatch: net != sum,
          netValue: net,
          netSumValue:sum,
        }
      }


      static CheckAcceptedNetSumValue(form: UntypedFormGroup) : CheckCostEstimationResult {

        let sum = 0;
        sum += form.controls['pdrLabourAccepted'].value;
        sum += form.controls['carBodyLabourAccepted'].value;
        sum += form.controls['additionalCostsAccepted'].value;
        sum += form.controls['paintLabourAccepted'].value;
        sum += form.controls['paintMaterialsAccepted'].value;
        sum += form.controls['originalPartsAccepted'].value;
        sum += form.controls['alternativePartsAccepted'].value;
        sum += form.controls['normaliesAccepted'].value;
        sum -= form.controls['discountForPartsAccepted'].value;

        const discountOutOfEstimation = form.controls['applyDiscountOutOfCostEstimation'].value;
        if(!discountOutOfEstimation){
          sum -= form.controls['discountAmountAccepted'].value;
        }

        sum = Math.round(sum * 100) / 100;

        let net = form.controls['acceptedAmount'].value;
        if(!net){
          net = 0;
        }

        const deductibles = form.controls['deductibleAmountAccepted'].value;
        const vat = ((net - deductibles) * Constants.DefaultVatRate *100) / 100;

        form.controls['finalNetAmountAccepted'].setValue(net - deductibles);
        form.controls['vatAmountAccepted'].setValue(vat);
        form.controls['grosAmountAccepted'].setValue(net - deductibles + vat);

        return {
          mismatch: net != sum,
          netValue: net,
          netSumValue:sum,
         
        }
      }

      static CheckForInvoiceNetSumValue(form: UntypedFormGroup) : CheckCostEstimationResult {

        let sum = 0;
        sum += form.controls['pdrLabourForInvoice'].value;
        sum += form.controls['carBodyLabourForInvoice'].value;
        sum += form.controls['additionalCostsForInvoice'].value;
        sum += form.controls['paintLabourForInvoice'].value;
        sum += form.controls['paintMaterialsForInvoice'].value;
        sum += form.controls['originalPartsForInvoice'].value;
        sum += form.controls['alternativePartsForInvoice'].value;
        sum += form.controls['normaliesForInvoice'].value;
        sum -= form.controls['discountForPartsForInvoice'].value;

        const discountOutOfEstimation = form.controls['applyDiscountOutOfCostEstimation'].value;
        if(!discountOutOfEstimation){
          sum -= form.controls['discountAmountForInvoice'].value;
        }
 
        sum = Math.round(sum * 100) / 100;
        
        form.controls['netAmountForInvoice'].setValue(sum);

        let net = form.controls['netAmountForInvoice'].value;
        if(!net){
          net = 0;
        }

        const deductibles = form.controls['deductibleAmountForInvoice'].value;
        const vat = ((net - deductibles) * Constants.DefaultVatRate *100) / 100;


        form.controls['finalNetAmountForInvoice'].setValue(net - deductibles);
        form.controls['vatAmountForInvoice'].setValue(vat);
        form.controls['grosAmountForInvoice'].setValue(net - deductibles + vat);

        return {
          mismatch: net != sum,
          netValue: net,
          netSumValue:sum,
        }
      }

      static CheckForInvoicePartialNetSumValue(form: UntypedFormGroup) : CheckCostEstimationResult {

        let sum = 0;
        sum += form.controls['pdrLabourForInvoicePartial'].value;
        sum += form.controls['carBodyLabourForInvoicePartial'].value;
        sum += form.controls['additionalCostsForInvoicePartial'].value;
        sum += form.controls['paintLabourForInvoicePartial'].value;
        sum += form.controls['paintMaterialsForInvoicePartial'].value;
        sum += form.controls['originalPartsForInvoicePartial'].value;
        sum += form.controls['alternativePartsForInvoicePartial'].value;
        sum += form.controls['normaliesForInvoicePartial'].value;
        sum -= form.controls['discountForPartsForInvoicePartial'].value;

        const discountOutOfEstimation = form.controls['applyDiscountOutOfCostEstimation'].value;
        if(!discountOutOfEstimation){
          sum -= form.controls['discountAmountForInvoicePartial'].value;
        }
 
        sum = Math.round(sum * 100) / 100;
        
        form.controls['netAmountForInvoicePartial'].setValue(sum);

        let net = form.controls['netAmountForInvoicePartial'].value;
        if(!net){
          net = 0;
        }

        const deductibles = form.controls['deductibleAmountForInvoicePartial'].value;
        const vat = ((net - deductibles) * Constants.DefaultVatRate *100) / 100;


        form.controls['finalNetAmountForInvoicePartial'].setValue(net - deductibles);
        form.controls['vatAmountForInvoicePartial'].setValue(vat);
        form.controls['grosAmountForInvoicePartial'].setValue(net - deductibles + vat);

        return {
          mismatch: net != sum,
          netValue: net,
          netSumValue:sum,
        }
      }
    
    static initCostEstimation(): CostEstimation {

        return {
            id: 0,
            claimIdentifier: '',
            costEstimationTypeId: CostEstimationType.Standard,
            costEstimationStatusId: CostEstimationStatus.InProgress,
            pdrLabour: 0.00,
            carBodyLabour: 0.00,
            additionalCosts: 0.00,
            discountForParts: 0.00,
            deductibleAmount: 0.00,
            grosAmount: 0.00,
            vatAmount: 0.00,
            paintLabour: 0.00,
            paintMaterials: 0.00,
            originalParts: 0.00,
            alternativeParts: 0.00,
            normalies: 0.00,
            discountAmount: 0.00,
            finalNetAmount: 0.00,
            netAmount: 0.00,
            applyDiscountOutOfCostEstimation:false,
            isInvoicePartialNeeded: false,
            costEstimationParts:[],
            passToPartnerState: false,
            forInvoiceConfirmation: false
        };
    }
}
